/** Public invitation acceptance page supporting both brand-new and existing users. */
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, setTenantSlug } from "@/api/client";
import { ApiError, unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { LanguageSwitcher } from "@/app/shell/AppShell";
import { LoadingState } from "@/shared/components/page";
import { SmartWatersMark, ThemeToggle } from "@/shared/ui/brand";
import { Button } from "@/shared/ui/button";
import { Field, Input } from "@/shared/ui/form";

/** Accept a workspace invitation (`/auth/accept-invite?tenant=...&token=...`). */
export function AcceptInvitePage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status, me, switchTenant } = useAuth();
  const search: { token?: string; tenant?: string } = useSearch({ strict: false });
  const token = (search.token ?? "").trim();

  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const previewQuery = useQuery({
    queryKey: ["invitation-preview", token],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/identity/invitations/preview", {
          params: { query: { token } },
        }),
      ),
    enabled: Boolean(token),
    retry: false,
  });

  const preview = previewQuery.data;
  const alreadySignedInAsInvitee =
    status === "authenticated" &&
    me?.email?.toLowerCase() !== undefined &&
    me.email.toLowerCase() === preview?.email.toLowerCase();

  const submit = async (e: { preventDefault(): void }): Promise<void> => {
    e.preventDefault();
    if (!preview) {
      return;
    }
    if (!alreadySignedInAsInvitee && !preview.user_exists && password !== confirmPassword) {
      setError(t("auth.passwordsDoNotMatch"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = unwrap(
        await api.POST("/api/v1/identity/invitations/accept", {
          body: {
            token,
            display_name: displayName.trim(),
            password: alreadySignedInAsInvitee ? null : password,
          },
        }),
      );
      setTenantSlug(result.tenant_slug);
      await switchTenant(result.tenant_slug);
      await navigate({ to: "/" });
    } catch (err) {
      if (err instanceof ApiError) {
        const firstFieldErr = err.problem.errors?.[0]?.message;
        setError(firstFieldErr ?? err.message);
      } else {
        setError(t("app.error"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-lg border border-t-3 border-t-brand bg-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-sidebar px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <SmartWatersMark className="size-7" />
            <div>
              <div className="font-bold tracking-tight text-foreground">{t("app.brand")}</div>
              <div className="text-xs text-muted-foreground">{t("auth.acceptInviteTitle")}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </div>
        <div className="space-y-4 p-5">
          {!token || previewQuery.isError ? (
            <div className="space-y-3 text-xs">
              <p role="alert" className="text-destructive">
                {t("auth.invalidInvitation")}
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/login">{t("auth.backToSignIn")}</Link>
              </Button>
            </div>
          ) : !preview ? (
            <LoadingState />
          ) : (
            <form onSubmit={(e) => void submit(e)} className="space-y-3.5" noValidate>
              <p className="text-xs text-muted-foreground">
                {t("auth.invitedToWorkspace", { workspace: preview.tenant_name })}
              </p>
              <Field id="invite-email" label={t("auth.email")}>
                <Input id="invite-email" type="email" value={preview.email} readOnly />
              </Field>
              {alreadySignedInAsInvitee ? (
                <p className="text-xs text-muted-foreground">{t("auth.acceptAsSignedInUser")}</p>
              ) : preview.user_exists ? (
                <>
                  <p className="text-xs text-muted-foreground">
                    {t("auth.existingAccountInviteHint")}
                  </p>
                  <Field id="invite-password" label={t("auth.password")}>
                    <Input
                      id="invite-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </Field>
                </>
              ) : (
                <>
                  <Field id="invite-display-name" label={t("app.name")}>
                    <Input
                      id="invite-display-name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      autoComplete="name"
                      required
                    />
                  </Field>
                  <Field
                    id="invite-password"
                    label={t("auth.password")}
                    hint={t("auth.passwordPolicyHint")}
                  >
                    <Input
                      id="invite-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                  </Field>
                  <Field id="invite-confirm-password" label={t("auth.confirmPassword")}>
                    <Input
                      id="invite-confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                  </Field>
                </>
              )}
              {error ? (
                <p role="alert" className="text-xs text-destructive">
                  {error}
                </p>
              ) : null}
              <Button
                type="submit"
                className="w-full"
                disabled={
                  busy ||
                  (!alreadySignedInAsInvitee &&
                    (!password || (!preview.user_exists && !confirmPassword)))
                }
              >
                {busy ? t("app.saving") : t("auth.acceptInviteButton")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
